import { API_BASE_URL } from "../app/utils";
import { authFetch } from "../app/common";

const parseResponse = async (response, fallbackMessage) => {
  let data = null;

  try {
    data = await response.json();
  } catch {
    // Successful actions may return an empty response body.
  }

  if (!response.ok) {
    throw new Error(
      data?.message ||
        data?.detail ||
        data?.error ||
        `${fallbackMessage} (${response.status})`
    );
  }

  return data;
};

/**
 * Paginated search via ElasticSearch. May return 403 "Access Denied" on some gateways if the
 * admin token is not allowed to call elastic-search — prefer user-store helpers below for
 * tagging / email resolution.
 */
export const getUsers = async (page, size, search) =>
  authFetch(`${API_BASE_URL}/parkit-elastic-search/v1/user/search?page=${page}&size=${size}${search ? '&term=' + encodeURIComponent(search) : ''}`, {
    crossDomain: true,
    method: "GET",
  }).then((res) => res.json());

/** Exact match on login email / username (userName is typically the email). Admin-safe. */
export const getUserByUserName = async (userName) => {
  const q = (userName || "").trim().toLowerCase();
  if (!q) return null;
  const res = await authFetch(
    `${API_BASE_URL}/user-store/v1/users?userName=${encodeURIComponent(q)}`,
    { crossDomain: true, method: "GET" }
  );
  if (res.status === 404) return null;
  if (!res.ok) return null;
  return res.json();
};

/** Partial match on username (contains, case-insensitive). Returns list. */
export const searchUsersByPartial = async (search) => {
  const term = (search || "").trim();
  if (term.length < 2) return [];
  const res = await authFetch(
    `${API_BASE_URL}/user-store/v1/users?search=${encodeURIComponent(term)}`,
    { crossDomain: true, method: "GET" }
  );
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

/** Resolve email → user without ElasticSearch (for bulk tagging). */
export const lookupUserByEmail = async (email) => {
  const e = (email || "").trim().toLowerCase();
  if (!e) return null;
  const direct = await getUserByUserName(e);
  if (direct) return direct;
  const list = await searchUsersByPartial(e);
  return (list || []).find((u) => (u.userName || "").toLowerCase() === e) || null;
};

export const getUser = async (id) =>
  authFetch(`${API_BASE_URL}/user-store/v1/users/${id}`, {
    crossDomain: true,
    method: "GET",
  }).then(res => res.json());

/** Same as getUser but returns null on 4xx/5xx (for admin lists that enrich rows). */
export const getUserSafe = async (id) => {
  if (!id) return null;
  const res = await authFetch(`${API_BASE_URL}/user-store/v1/users/${id}`, {
    crossDomain: true,
    method: "GET",
  });
  if (!res.ok) return null;
  return res.json();
};

export const updateUser = (id, data) =>
  authFetch(`${API_BASE_URL}/user-store/v1/users/${id}`, {
    crossDomain: true,
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }).then(res => res.json());

export const getDeleteInquiryRequests = async (page, size) => {
  const response = await authFetch(`${API_BASE_URL}/user-store/v1/delete-inquiries?page=${page}&size=${size}`, {
    crossDomain: true,
    method: "GET",
  });

  return parseResponse(response, "Failed to load delete user requests");
};

export const approveDeleteInquiryRequest = async (id) => {
  const response = await authFetch(`${API_BASE_URL}/user-store/v1/users/${id}/approve-delete`, {
    crossDomain: true,
    method: "POST",
  });

  return parseResponse(response, "Failed to approve delete user request");
};
